import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ColumnDef, flexRender, getCoreRowModel, useReactTable } from "@tanstack/react-table";
import { ArrowLeft, ArrowRight, Check, Info, Loader2, Pencil, Plus, Power, Trash2, X } from "lucide-react";
import React, { useRef, useMemo, useState } from "react";
import { Button } from "../../../../../components/ui/button";
import { Badge } from "../../../../../components/ui/badge";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "../../../../../components/ui/dialog";
import { Input } from "../../../../../components/ui/input";
import { Label } from "../../../../../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../../../../components/ui/select";
import { Textarea } from "../../../../../components/ui/textarea";
import { Switch } from "../../../../../components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../../../../components/ui/tabs";
import { Separator } from "../../../../../components/ui/separator";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "../../../../../components/ui/tooltip";
import { useWorkspaceObjectSchema } from "../../../../../hooks/QueryHooks";
import {
  createComputedLabel,
  createComputedMetric,
  deleteComputedLabel,
  deleteComputedMetric,
  listComputationRuns,
  listComputedLabels,
  listComputedMetrics,
  triggerComputedTraitsRecompute,
  updateComputedLabel,
  updateComputedMetric,
} from "../../../../../services/computedTraits/computedTraitsEndpoints";
import type {
  ComputationRun,
  ComputedFormula,
  ComputedLabel,
  ComputedLabelCreateRequest,
  ComputedLabelUpdateRequest,
  ComputedMetric,
  ComputedMetricCreateRequest,
  ComputedMetricUpdateRequest,
  ComputedDisplayFormat,
  ComputedMetricCategory,
  FormulaOperand,
  LabelOption,
  LabelRule,
  Predicate,
  PredicateGroup,
} from "../../../../../services/computedTraits/computedTraitsEndpoints.interface";
import {
  CATEGORY_VALUES,
  coerceSelectValue,
  DATA_TYPE_VALUES,
  DISPLAY_FORMAT_VALUES,
  ENTITY_TYPE_VALUES,
  EVENT_OPERATIONS,
  FORMULA_EXPRESSIONS,
  GROWTH_COMPARISON_VALUES,
  isOneOf,
  METRIC_FORMULA_TYPES,
  OBJECT_OPERATIONS,
  OPERAND_KIND_VALUES,
  PREDICATE_OPERATORS,
  PREDICATE_TYPE_VALUES,
  RULE_MATCH_VALUES,
} from "../../../../../types/computedTraits";
import type {
  ActiveComputedTraitTab,
  LabelDraft,
  LabelRuleDraft,
  MetricDraft,
  MetricFormulaType,
  OperandDraft,
  PredicateDraft,
  RuleMatchType,
} from "../../../../../types/computedTraits";
import useWorkspaceStore from "../../../../../store/workspace/workspaceStore";
import { toast } from "sonner";

/* ──────────────────── Helpers ──────────────────── */

const normalizeName = (value: string) => value.toLowerCase().replace(/[^a-z0-9_]/g, "_").replace(/^_+/, "");
const createDraftId = () => {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }
  return `${Date.now()}_${Math.random().toString(36).slice(2, 9)}`;
};

const splitComma = (value: string): string[] =>
  value
    .split(",")
    .map((item) => item.trim())
    .filter(Boolean);

const asNumber = (value: string): number | undefined => {
  if (!value.trim()) return undefined;
  const parsed = Number(value);
  if (Number.isNaN(parsed)) return undefined;
  return parsed;
};

type ApiError = {
  response?: { data?: { detail?: string | Array<{ msg?: string }> } };
  message?: string;
};

const getErrorMessage = (error: unknown) => {
  const detail = (error as ApiError)?.response?.data?.detail;
  if (Array.isArray(detail)) return detail.map((d) => d?.msg || String(d)).join(", ");
  if (typeof detail === "string") return detail;
  return (error as ApiError)?.message || "Something went wrong.";
};

const formatDateTime = (value?: string | null) => {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";
  return new Intl.DateTimeFormat("en-US", {
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  }).format(date);
};

const formatDuration = (durationMs?: number | null) => {
  if (!durationMs || durationMs <= 0) return "—";
  if (durationMs < 1000) return `${durationMs}ms`;
  return `${(durationMs / 1000).toFixed(2)}s`;
};

// eslint-disable-next-line no-alert
const confirmAction = (message: string) => window.confirm(message);

const isPredicateGroup = (predicate: unknown): predicate is PredicateGroup =>
  !!predicate
  && typeof predicate === "object"
  && "query_type" in predicate
  && Array.isArray((predicate as { predicates?: unknown }).predicates);

const ensureDisplayFormat = (value?: string | null): ComputedDisplayFormat => {
  const normalized = value ?? null;
  return isOneOf(normalized, DISPLAY_FORMAT_VALUES) ? normalized : "number";
};

const ensureCategory = (value?: string | null): ComputedMetricCategory => {
  const normalized = value ?? null;
  return isOneOf(normalized, CATEGORY_VALUES) ? normalized : "activity";
};

const ensureMetricFormulaType = (value?: string | null): MetricFormulaType => {
  const normalized = value ?? null;
  return isOneOf(normalized, METRIC_FORMULA_TYPES) ? normalized : "event_aggregation";
};

const ensureRuleMatchType = (value?: string | null): RuleMatchType => {
  const normalized = value ?? null;
  return isOneOf(normalized, RULE_MATCH_VALUES) ? normalized : "all";
};

/* ──────────────────── Constants ──────────────────── */

const METRIC_MODAL_STEPS = [1, 2, 3] as const;
type MetricModalStep = (typeof METRIC_MODAL_STEPS)[number];

const FORMULA_TYPE_LABELS: Record<string, string> = {
  event_aggregation: "Event Aggregation",
  event_growth: "Event Growth",
  object_aggregation: "Object Aggregation",
  formula: "Derived",
};

const FORMULA_TYPE_HELP: Record<MetricFormulaType, string> = {
  event_aggregation: "Count, sum, or aggregate events over a time window. Example: count all login events in the last 30 days.",
  event_growth: "Measure how a metric has changed between two time periods. Example: % change in active users month-over-month.",
  object_aggregation: "Aggregate data from related objects like deals or meetings. Example: count of open deals per account.",
  formula: "Combine existing metrics with math operations. Example: divide total_actions by active_users to get actions per user.",
};

const METRIC_FORMULA_TYPE_CONFIG: ReadonlyArray<{
  value: MetricFormulaType;
  label: string;
  description: string;
}> = [
  { value: "event_aggregation", label: "Event Aggregation", description: "Aggregate events over a time window" },
  { value: "event_growth", label: "Event Growth", description: "Measure change between time periods" },
  { value: "object_aggregation", label: "Object Aggregation", description: "Aggregate related object fields" },
  { value: "formula", label: "Derived", description: "Combine metrics with math" },
];

/* ──────────────────── Draft factories ──────────────────── */

const getWorkspaceSchemaObjects = (value: unknown): unknown[] => {
  if (!value || typeof value !== "object" || !("objects" in value)) return [];
  const possibleObjects = value.objects;
  return Array.isArray(possibleObjects) ? possibleObjects : [];
};

const createEmptyPredicateDraft = (): PredicateDraft => ({
  id: createDraftId(),
  field: "",
  operator: "eq",
  type: "string",
  value: "",
});

const flattenPredicates = (group: PredicateGroup): Predicate[] => {
  const acc: Predicate[] = [];
  group.predicates.forEach((item) => {
    if (isPredicateGroup(item)) {
      acc.push(...flattenPredicates(item));
      return;
    }
    acc.push(item);
  });
  return acc;
};

const createDefaultMetricDraft = (): MetricDraft => ({
  name: "",
  display_name: "",
  description: "",
  entity_type: "accounts",
  data_type: "float",
  display_format: "number",
  category: "activity",
  compute_priority: 100,
  track_history: false,
  is_active: true,

  formula_type: "event_aggregation",
  event_names: "*",
  event_operation: "count",
  event_days: "",
  event_days_offset: 0,
  event_comparison: "percentage_change",

  object_slug: "",
  object_operation: "count",
  object_field: "",
  object_days: "",

  formula_expression: "add",
  operands: [
    { id: createDraftId(), kind: "ref", value: "" },
    { id: createDraftId(), kind: "raw", value: "0" },
  ],
});

const createDefaultLabelDraft = (): LabelDraft => ({
  name: "",
  display_name: "",
  description: "",
  entity_type: "accounts",
  default_label: "unknown",
  track_history: true,
  is_enabled: true,
  options: [
    { id: createDraftId(), value: "healthy", display_name: "Healthy", color: "#22c55e" },
    { id: createDraftId(), value: "at_risk", display_name: "At Risk", color: "#f59e0b" },
    { id: createDraftId(), value: "unknown", display_name: "Unknown", color: "#6b7280" },
  ],
  rules: [
    {
      id: createDraftId(),
      label: "healthy",
      match_type: "all",
      predicates: [{ ...createEmptyPredicateDraft(), field: "c__health_score", operator: "gte", type: "float", value: "7" }],
    },
  ],
});

/* ──────────────────── Converters ──────────────────── */

const metricToDraft = (metric: ComputedMetric): MetricDraft => {
  const base = createDefaultMetricDraft();
  const formulaType = ensureMetricFormulaType(metric.formula?.type);
  const next: MetricDraft = {
    ...base,
    name: metric.name,
    display_name: metric.display_name,
    description: metric.description || "",
    entity_type: metric.entity_type,
    data_type: metric.data_type,
    display_format: ensureDisplayFormat(metric.display_format),
    category: ensureCategory(metric.category),
    compute_priority: metric.compute_priority || 100,
    track_history: Boolean(metric.track_history),
    is_active: Boolean(metric.is_active),
    formula_type: formulaType,
  };

  if (formulaType === "event_aggregation" && metric.formula.type === "event_aggregation") {
    const { formula } = metric;
    next.event_names = Array.isArray(formula.event) ? formula.event.join(", ") : formula.event || "*";
    next.event_operation = formula.operation || "count";
    next.event_days = formula.days ? String(formula.days) : "";
    next.event_days_offset = Number(formula.days_offset || 0);
  } else if (formulaType === "event_growth" && metric.formula.type === "event_growth") {
    const { formula } = metric;
    next.event_names = Array.isArray(formula.event) ? formula.event.join(", ") : formula.event || "*";
    next.event_operation = formula.operation || "count";
    next.event_days = formula.days ? String(formula.days) : "";
    next.event_comparison = formula.comparison || "percentage_change";
  } else if (formulaType === "object_aggregation" && metric.formula.type === "object_aggregation") {
    const { formula } = metric;
    next.object_slug = formula.object_slug || "";
    next.object_operation = formula.operation || "count";
    next.object_field = formula.field || "";
    next.object_days = formula.days ? String(formula.days) : "";
  } else if (formulaType === "formula" && metric.formula.type === "formula") {
    const { formula } = metric;
    next.formula_expression = formula.expression || "add";
    next.operands = Array.isArray(formula.operands) && formula.operands.length > 0
      ? formula.operands.map((operand): OperandDraft => {
        if (operand.ref) return { id: createDraftId(), kind: "ref", value: String(operand.ref) };
        if (operand.prop) return { id: createDraftId(), kind: "prop", value: String(operand.prop) };
        if (operand.raw !== undefined && operand.raw !== null) return { id: createDraftId(), kind: "raw", value: String(operand.raw) };
        return { id: createDraftId(), kind: "ref", value: "" };
      })
      : base.operands;
  }

  return next;
};

const labelToDraft = (label: ComputedLabel): LabelDraft => {
  const base = createDefaultLabelDraft();
  const options = (label.label_options || []).map((option) => ({
    id: createDraftId(),
    value: option.value,
    display_name: option.display_name,
    color: option.color,
  }));
  const rules = (label.rules || [])
    .filter((rule) => rule.filter)
    .map((rule) => {
      const predicates = flattenPredicates(rule.filter).map((predicate) => ({
        id: createDraftId(),
        field: predicate.field,
        operator: predicate.operator,
        type: predicate.type || "string",
        value: predicate.value === undefined || predicate.value === null
          ? ""
          : Array.isArray(predicate.value)
            ? predicate.value.join(",")
            : String(predicate.value),
      }));
      return {
        id: createDraftId(),
        label: rule.label,
        match_type: ensureRuleMatchType(rule.filter.query_type === "OR" ? "any" : "all"),
        predicates: predicates.length > 0 ? predicates : base.rules[0].predicates,
      };
    });

  return {
    ...base,
    name: label.name,
    display_name: label.display_name,
    description: label.description || "",
    entity_type: label.entity_type,
    default_label: label.default_label || options?.[0]?.value || "unknown",
    track_history: Boolean(label.track_history),
    is_enabled: Boolean(label.is_enabled),
    options: options.length > 0 ? options : base.options,
    rules: rules.length > 0 ? rules : base.rules,
  };
};

/* ──────────────────── Builders ──────────────────── */

const parsePredicateValue = (predicate: PredicateDraft) => {
  if (predicate.operator === "known" || predicate.operator === "unknown") return undefined;

  if (predicate.operator === "in") {
    return splitComma(predicate.value).map((item) => {
      if (predicate.type === "int" || predicate.type === "float") return Number(item);
      if (predicate.type === "boolean") return item.toLowerCase() === "true";
      return item;
    });
  }

  if (predicate.operator === "range") {
    const values = splitComma(predicate.value).slice(0, 2);
    return values.map((item) => {
      if (predicate.type === "int" || predicate.type === "float") return Number(item);
      return item;
    });
  }

  if (predicate.type === "int" || predicate.type === "float") return Number(predicate.value);
  if (predicate.type === "boolean") return predicate.value.toLowerCase() === "true";
  return predicate.value;
};

const buildMetricFormula = (draft: MetricDraft): ComputedFormula => {
  if (draft.formula_type === "event_aggregation") {
    const events = splitComma(draft.event_names);
    return {
      type: "event_aggregation",
      event: draft.event_names.trim() === "*" ? "*" : (events.length > 1 ? events : events[0] || "*"),
      operation: draft.event_operation,
      days: asNumber(draft.event_days),
      days_offset: Number(draft.event_days_offset || 0),
    };
  }

  if (draft.formula_type === "event_growth") {
    const events = splitComma(draft.event_names);
    const days = asNumber(draft.event_days);
    if (!days || days <= 0) {
      throw new Error("Event growth formulas require a valid number of days.");
    }
    return {
      type: "event_growth",
      event: draft.event_names.trim() === "*" ? "*" : (events.length > 1 ? events : events[0] || "*"),
      operation: draft.event_operation,
      days,
      comparison: draft.event_comparison,
    };
  }

  if (draft.formula_type === "object_aggregation") {
    if (!draft.object_slug.trim()) {
      throw new Error("Object aggregation formulas require an object slug.");
    }
    if (["sum", "avg", "min", "max"].includes(draft.object_operation) && !draft.object_field.trim()) {
      throw new Error("Selected object operation requires a field.");
    }
    return {
      type: "object_aggregation",
      object_slug: draft.object_slug.trim(),
      operation: draft.object_operation,
      field: draft.object_field.trim() || undefined,
      days: asNumber(draft.object_days),
    };
  }

  const validOperands = draft.operands
    .filter((operand) => operand.value.trim().length > 0)
    .map((operand): FormulaOperand => {
      if (operand.kind === "ref") return { ref: operand.value.trim() };
      if (operand.kind === "prop") return { prop: operand.value.trim() };
      return { raw: Number(operand.value) };
    });

  if (validOperands.length < 2) {
    throw new Error("Derived formulas require at least two operands.");
  }

  return {
    type: "formula",
    expression: draft.formula_expression,
    operands: validOperands,
  };
};

const buildLabelRules = (draft: {
  rules: LabelRuleDraft[];
  options: Array<Pick<LabelOption, "value" | "display_name">>;
}): LabelRule[] => draft.rules.map((rule) => {
  const option = draft.options.find((opt) => opt.value === rule.label);
  const predicates: Predicate[] = rule.predicates.map((predicate) => ({
    field: predicate.field.trim(),
    operator: predicate.operator,
    type: predicate.type,
    value: parsePredicateValue(predicate),
  }));
  return {
    label: rule.label,
    display_name: option?.display_name || rule.label,
    filter: {
      query_type: rule.match_type === "any" ? "OR" : "AND",
      predicates,
    },
  };
});

/* ──────────────────── Table column builders ──────────────────── */

const buildMetricColumns = ({
  onEdit,
  onToggle,
}: {
  onEdit: (metric: ComputedMetric) => void;
  onToggle: (metric: ComputedMetric) => void;
}): ColumnDef<ComputedMetric>[] => [
  {
    id: "display_name",
    header: "Display Name",
    accessorKey: "display_name",
    size: 280,
    cell: ({ row }) => (
      <div>
        <span className="text-[13px] font-medium text-foreground">{row.original.display_name}</span>
        <span className="block text-[11px] font-mono text-muted-foreground">{row.original.column_name}</span>
      </div>
    ),
  },
  {
    id: "entity",
    header: "Entity",
    size: 100,
    accessorKey: "entity_type",
    cell: ({ row }) => (
      <Badge variant="secondary" className="text-[11px]">{row.original.entity_type}</Badge>
    ),
  },
  {
    id: "formula",
    header: "Formula Type",
    size: 160,
    accessorFn: (row) => row.formula?.type || "—",
    cell: (info) => (
      <span className="text-[13px] text-muted-foreground">
        {FORMULA_TYPE_LABELS[String(info.getValue())] || String(info.getValue())}
      </span>
    ),
  },
  {
    id: "status",
    header: "Status",
    size: 100,
    accessorFn: (row) => Boolean(row.is_active),
    cell: ({ row }) => (
      <Badge variant="statusPill" status={row.original.is_active ? "active" : "pending"}>
        {row.original.is_active ? "Active" : "Paused"}
      </Badge>
    ),
  },
  {
    id: "actions",
    header: "",
    size: 80,
    cell: ({ row }) => (
      <div className="flex items-center justify-end gap-0.5">
        <Button
          variant="ghost"
          size="icon"
          className="h-7 w-7"
          aria-label="Edit metric"
          onClick={(event) => {
            event.stopPropagation();
            onEdit(row.original);
          }}
        >
          <Pencil className="h-3.5 w-3.5" />
        </Button>
        <Button
          variant="ghost"
          size="icon"
          className={`h-7 w-7 ${row.original.is_system ? "opacity-30 cursor-not-allowed" : row.original.is_active ? "text-muted-foreground hover:text-foreground" : "text-primary hover:text-primary"}`}
          aria-label={row.original.is_active ? "Disable metric" : "Enable metric"}
          onClick={(event) => {
            event.stopPropagation();
            if (!row.original.is_system) onToggle(row.original);
          }}
        >
          <Power className="h-3.5 w-3.5" />
        </Button>
      </div>
    ),
  },
];

const buildLabelColumns = ({
  onEdit,
  onToggle,
}: {
  onEdit: (label: ComputedLabel) => void;
  onToggle: (label: ComputedLabel) => void;
}): ColumnDef<ComputedLabel>[] => [
  {
    id: "display_name",
    header: "Display Name",
    size: 280,
    accessorKey: "display_name",
    cell: ({ row }) => (
      <div>
        <span className="text-[13px] font-medium text-foreground">{row.original.display_name}</span>
        <span className="block text-[11px] font-mono text-muted-foreground">{row.original.column_name}</span>
      </div>
    ),
  },
  {
    id: "entity",
    header: "Entity",
    size: 100,
    accessorKey: "entity_type",
    cell: ({ row }) => (
      <Badge variant="secondary" className="text-[11px]">{row.original.entity_type}</Badge>
    ),
  },
  {
    id: "labels",
    header: "Labels",
    size: 120,
    accessorFn: (row) => row.label_options?.length || 0,
    cell: ({ row }) => (
      <div className="flex items-center gap-1">
        {(row.original.label_options || []).map((option) => (
          <div key={option.value} className="h-2.5 w-2.5 rounded-full" style={{ backgroundColor: option.color }} />
        ))}
        <span className="ml-1.5 text-[11px] text-muted-foreground">{row.original.label_options?.length || 0}</span>
      </div>
    ),
  },
  {
    id: "status",
    header: "Status",
    size: 100,
    accessorFn: (row) => Boolean(row.is_enabled),
    cell: ({ row }) => (
      <Badge variant="statusPill" status={row.original.is_enabled ? "active" : "pending"}>
        {row.original.is_enabled ? "Active" : "Paused"}
      </Badge>
    ),
  },
  {
    id: "actions",
    header: "",
    size: 80,
    cell: ({ row }) => (
      <div className="flex items-center justify-end gap-0.5">
        <Button
          variant="ghost"
          size="icon"
          className="h-7 w-7"
          aria-label="Edit label"
          onClick={(event) => {
            event.stopPropagation();
            onEdit(row.original);
          }}
        >
          <Pencil className="h-3.5 w-3.5" />
        </Button>
        <Button
          variant="ghost"
          size="icon"
          className={`h-7 w-7 ${row.original.is_system ? "opacity-30 cursor-not-allowed" : row.original.is_enabled ? "text-muted-foreground hover:text-foreground" : "text-primary hover:text-primary"}`}
          aria-label={row.original.is_enabled ? "Disable label" : "Enable label"}
          onClick={(event) => {
            event.stopPropagation();
            if (!row.original.is_system) onToggle(row.original);
          }}
        >
          <Power className="h-3.5 w-3.5" />
        </Button>
      </div>
    ),
  },
];

/* ──────────────────── Inline field helper ──────────────────── */

const FieldRow = ({ label, children, hint }: { label: string; children: React.ReactNode; hint?: string }) => (
  <div className="space-y-1.5">
    <Label className="text-[12px]">{label}</Label>
    {children}
    {hint && <p className="text-[11px] text-muted-foreground">{hint}</p>}
  </div>
);

/* ──────────────────── TagsField — lightweight multi-value input ──────────────────── */

const TagsField = ({
  label,
  hint,
  values,
  options,
  onChange,
}: {
  label: string;
  hint?: string;
  values: string[];
  options: string[];
  onChange: (next: string[]) => void;
}) => {
  const [inputValue, setInputValue] = useState("");
  const addValue = (v: string) => {
    const trimmed = v.trim();
    if (!trimmed || values.includes(trimmed)) return;
    if (trimmed === "*") {
      onChange(["*"]);
    } else {
      onChange([...values.filter((item) => item !== "*"), trimmed]);
    }
    setInputValue("");
  };
  const filteredOptions = options.filter(
    (opt) => !values.includes(opt) && opt.toLowerCase().includes(inputValue.toLowerCase()),
  );

  return (
    <div className="space-y-1.5">
      <Label className="text-[12px]">{label}</Label>
      <div className="flex flex-wrap items-center gap-1 rounded-[5px] border border-border-chip bg-card px-2 py-1.5 min-h-[32px]">
        {values.map((v) => (
          <span key={v} className="inline-flex items-center gap-1 rounded bg-muted px-1.5 py-0.5 text-[12px]">
            {v}
            <button type="button" className="text-muted-foreground hover:text-foreground" onClick={() => onChange(values.filter((item) => item !== v))}>
              <X className="h-3 w-3" />
            </button>
          </span>
        ))}
        <input
          className="flex-1 min-w-[80px] bg-transparent text-[13px] outline-none placeholder:text-muted-foreground"
          placeholder="Type or select…"
          value={inputValue}
          onChange={(e) => setInputValue(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              addValue(inputValue);
            }
            if (e.key === "Backspace" && !inputValue && values.length > 0) {
              onChange(values.slice(0, -1));
            }
          }}
        />
      </div>
      {inputValue && filteredOptions.length > 0 && (
        <div className="max-h-[160px] overflow-y-auto rounded-md border border-border bg-card p-1 shadow-sm">
          {filteredOptions.slice(0, 15).map((opt) => (
            <button
              key={opt}
              type="button"
              className="w-full rounded px-2 py-1 text-left text-[12px] hover:bg-muted"
              onClick={() => addValue(opt)}
            >
              {opt}
            </button>
          ))}
        </div>
      )}
      {hint && <p className="text-[11px] text-muted-foreground">{hint}</p>}
    </div>
  );
};

/* ──────────────────── Main Component ──────────────────── */

const ComputedTraitsPage = () => {
  const workspaceId = useWorkspaceStore((state) => state.workspace?.id);
  const queryClient = useQueryClient();
  const requireWorkspaceId = () => {
    if (!workspaceId) throw new Error("Workspace not found");
    return workspaceId;
  };

  const [activeTab, setActiveTab] = useState<ActiveComputedTraitTab>("metrics");

  const [metricModalOpen, setMetricModalOpen] = useState(false);
  const [metricModalStep, setMetricModalStep] = useState<MetricModalStep>(1);
  const [metricDraft, setMetricDraft] = useState<MetricDraft>(createDefaultMetricDraft());
  const [editingMetric, setEditingMetric] = useState<ComputedMetric | null>(null);

  const [labelModalOpen, setLabelModalOpen] = useState(false);
  const [labelDraft, setLabelDraft] = useState<LabelDraft>(createDefaultLabelDraft());
  const [editingLabel, setEditingLabel] = useState<ComputedLabel | null>(null);
  const lastFullRecomputeAt = useRef<number>(0);

  const closeMetricModal = () => {
    setMetricModalOpen(false);
    setMetricModalStep(1);
  };

  const { data: workspaceSchemaData } = useWorkspaceObjectSchema({
    workspaceId: workspaceId || "",
    enabled: !!workspaceId,
  });

  const objectOptions = useMemo(
    () => {
      const rawObjects = getWorkspaceSchemaObjects(workspaceSchemaData);
      return rawObjects
        .map((object) => (
          typeof object === "object" && object !== null && "api_slug" in object && typeof object.api_slug === "string"
            ? object.api_slug
            : ""
        ))
        .filter((slug): slug is string => !!slug && slug !== "accounts" && slug !== "users")
        .map((slug) => ({ value: slug, label: slug }));
    },
    [workspaceSchemaData],
  );

  const metricEventNameOptions = useMemo(
    () => {
      const metricsByObjectSlug = (workspaceSchemaData as { metricsByObjectSlug?: Record<string, { list?: unknown[] }> } | undefined)
        ?.metricsByObjectSlug || {};

      const scopedMetrics = metricsByObjectSlug[metricDraft.entity_type]?.list || [];
      const globalMetrics = metricsByObjectSlug.global?.list || [];
      const values = [...scopedMetrics, ...globalMetrics]
        .map((metric) => {
          if (!metric || typeof metric !== "object") return "";
          if ("field_name" in metric && typeof metric.field_name === "string") return metric.field_name.trim();
          if ("name" in metric && typeof metric.name === "string") return metric.name.trim();
          return "";
        })
        .filter(Boolean);
      return Array.from(new Set(values)).sort((a, b) => a.localeCompare(b));
    },
    [metricDraft.entity_type, workspaceSchemaData],
  );

  /* ── Queries ── */

  const metricsQuery = useQuery<ComputedMetric[]>({
    queryKey: ["computed-metrics", workspaceId],
    queryFn: async () => {
      const response = await listComputedMetrics({ workspace_id: requireWorkspaceId() });
      return response.data || [];
    },
    enabled: !!workspaceId,
    staleTime: 1000 * 15,
  });

  const labelsQuery = useQuery<ComputedLabel[]>({
    queryKey: ["computed-labels", workspaceId],
    queryFn: async () => {
      const response = await listComputedLabels({ workspace_id: requireWorkspaceId() });
      return response.data || [];
    },
    enabled: !!workspaceId,
    staleTime: 1000 * 15,
  });

  const runsQuery = useQuery<ComputationRun[]>({
    queryKey: ["computed-trait-runs", workspaceId],
    queryFn: async () => {
      const response = await listComputationRuns({ workspace_id: requireWorkspaceId(), limit: 20 });
      return response.data || [];
    },
    enabled: !!workspaceId,
    staleTime: 1000 * 10,
  });

  const refreshTraitsData = () => {
    queryClient.invalidateQueries({ queryKey: ["computed-metrics", workspaceId] });
    queryClient.invalidateQueries({ queryKey: ["computed-labels", workspaceId] });
    queryClient.invalidateQueries({ queryKey: ["computed-trait-runs", workspaceId] });
    queryClient.invalidateQueries({ queryKey: ["workspace-object-schema", workspaceId] });
  };

  /* ── Mutations ── */

  const createMetricMutation = useMutation({
    mutationFn: async (payload: ComputedMetricCreateRequest) =>
      createComputedMetric({ workspace_id: requireWorkspaceId(), payload }),
    onSuccess: () => {
      toast.success("Computed metric created.");
      refreshTraitsData();
      closeMetricModal();
    },
    onError: (error) => {
      toast.error(getErrorMessage(error));
    },
  });

  const updateMetricMutation = useMutation({
    mutationFn: async ({ metricId, payload }: { metricId: string; payload: ComputedMetricUpdateRequest }) =>
      updateComputedMetric({ workspace_id: requireWorkspaceId(), metric_id: metricId, payload }),
    onSuccess: () => {
      toast.success("Computed metric updated.");
      refreshTraitsData();
      closeMetricModal();
    },
    onError: (error) => {
      toast.error(getErrorMessage(error));
    },
  });

  const deleteMetricMutation = useMutation({
    mutationFn: async (metricId: string) =>
      deleteComputedMetric({ workspace_id: requireWorkspaceId(), metric_id: metricId }),
    onSuccess: () => {
      toast.success("Computed metric deleted.");
      refreshTraitsData();
    },
    onError: (error) => {
      toast.error(getErrorMessage(error));
    },
  });

  const createLabelMutation = useMutation({
    mutationFn: async (payload: ComputedLabelCreateRequest) =>
      createComputedLabel({ workspace_id: requireWorkspaceId(), payload }),
    onSuccess: () => {
      toast.success("Computed label created.");
      refreshTraitsData();
      setLabelModalOpen(false);
    },
    onError: (error) => {
      toast.error(getErrorMessage(error));
    },
  });

  const updateLabelMutation = useMutation({
    mutationFn: async ({ labelId, payload }: { labelId: string; payload: ComputedLabelUpdateRequest }) =>
      updateComputedLabel({ workspace_id: requireWorkspaceId(), label_id: labelId, payload }),
    onSuccess: () => {
      toast.success("Computed label updated.");
      refreshTraitsData();
      setLabelModalOpen(false);
    },
    onError: (error) => {
      toast.error(getErrorMessage(error));
    },
  });

  const deleteLabelMutation = useMutation({
    mutationFn: async (labelId: string) =>
      deleteComputedLabel({ workspace_id: requireWorkspaceId(), label_id: labelId }),
    onSuccess: () => {
      toast.success("Computed label deleted.");
      refreshTraitsData();
    },
    onError: (error) => {
      toast.error(getErrorMessage(error));
    },
  });

  const triggerRecomputeMutation = useMutation({
    mutationFn: async (fullRecompute: boolean) =>
      triggerComputedTraitsRecompute({
        workspace_id: requireWorkspaceId(),
        payload: { full_recompute: fullRecompute },
      }),
    onSuccess: (response, fullRecompute) => {
      if (fullRecompute) {
        lastFullRecomputeAt.current = Date.now();
      }
      toast.success(`Recompute queued (${response.data?.run_type || "incremental"}).`);
      refreshTraitsData();
    },
    onError: (error) => {
      const status = (error as ApiError & { response?: { status?: number } })?.response?.status;
      if (status === 409) {
        lastFullRecomputeAt.current = Date.now();
        toast.error("A computation is already running. Please wait for it to complete.");
      } else {
        toast.error(getErrorMessage(error));
      }
    },
  });

  /* ── Modal openers ── */

  const openCreateMetric = () => {
    setEditingMetric(null);
    setMetricDraft(createDefaultMetricDraft());
    setMetricModalStep(1);
    setMetricModalOpen(true);
  };

  const openEditMetric = (metric: ComputedMetric) => {
    setEditingMetric(metric);
    setMetricDraft(metricToDraft(metric));
    setMetricModalStep(1);
    setMetricModalOpen(true);
  };

  const openCreateLabel = () => {
    setEditingLabel(null);
    setLabelDraft(createDefaultLabelDraft());
    setLabelModalOpen(true);
  };

  const openEditLabel = (label: ComputedLabel) => {
    setEditingLabel(label);
    setLabelDraft(labelToDraft(label));
    setLabelModalOpen(true);
  };

  /* ── Save handlers ── */

  const handleSaveMetric = () => {
    if (!workspaceId) return;
    if (!metricDraft.display_name.trim()) {
      toast.error("Display name is required.");
      return;
    }

    if (!editingMetric) {
      if (!metricDraft.name.trim()) {
        toast.error("Name is required.");
        return;
      }
      if (!/^[a-z][a-z0-9_]*$/.test(metricDraft.name.trim())) {
        toast.error("Name must be lowercase snake_case and start with a letter.");
        return;
      }
    }

    let formula: ComputedFormula;
    try {
      formula = buildMetricFormula(metricDraft);
    } catch (error: unknown) {
      toast.error(getErrorMessage(error) || "Invalid formula configuration.");
      return;
    }

    if (!editingMetric) {
      const payload: ComputedMetricCreateRequest = {
        name: metricDraft.name.trim(),
        display_name: metricDraft.display_name.trim(),
        description: metricDraft.description.trim() || undefined,
        entity_type: metricDraft.entity_type,
        data_type: metricDraft.data_type,
        display_format: metricDraft.display_format,
        category: metricDraft.category,
        track_history: metricDraft.track_history,
        compute_priority: metricDraft.compute_priority,
        formula,
      };
      createMetricMutation.mutate(payload);
      return;
    }

    const payload: ComputedMetricUpdateRequest = {
      display_name: metricDraft.display_name.trim(),
      description: metricDraft.description.trim() || undefined,
      formula,
      compute_priority: metricDraft.compute_priority,
      track_history: metricDraft.track_history,
      is_active: metricDraft.is_active,
    };
    updateMetricMutation.mutate({ metricId: editingMetric.id, payload });
  };

  const handleSaveLabel = () => {
    if (!workspaceId) return;
    if (!labelDraft.display_name.trim()) {
      toast.error("Display name is required.");
      return;
    }

    if (!editingLabel) {
      if (!labelDraft.name.trim()) {
        toast.error("Name is required.");
        return;
      }
      if (!/^[a-z][a-z0-9_]*$/.test(labelDraft.name.trim())) {
        toast.error("Name must be lowercase snake_case and start with a letter.");
        return;
      }
    }

    const options: LabelOption[] = labelDraft.options
      .map((option) => ({
        value: normalizeName(option.value),
        display_name: option.display_name.trim(),
        color: option.color.toLowerCase(),
      }))
      .filter((option) => option.value && option.display_name);

    if (options.length < 2) {
      toast.error("At least two label options are required.");
      return;
    }

    const optionValues = new Set(options.map((option) => option.value));
    if (!optionValues.has(labelDraft.default_label)) {
      toast.error("Default label must match one of the options.");
      return;
    }

    const invalidRule = labelDraft.rules.find((rule) => {
      if (!optionValues.has(rule.label)) return true;
      if (rule.predicates.length === 0) return true;
      return rule.predicates.some((predicate) => {
        if (!predicate.field.trim()) return true;
        if ((predicate.operator === "known" || predicate.operator === "unknown")) return false;
        return !predicate.value.trim();
      });
    });
    if (invalidRule) {
      toast.error("Each rule must have valid predicates and mapped labels.");
      return;
    }

    const rules = buildLabelRules({
      ...labelDraft,
      options,
    });

    if (!editingLabel) {
      const payload: ComputedLabelCreateRequest = {
        name: labelDraft.name.trim(),
        display_name: labelDraft.display_name.trim(),
        description: labelDraft.description.trim() || undefined,
        entity_type: labelDraft.entity_type,
        label_type: "standard",
        rules,
        default_label: labelDraft.default_label,
        label_options: options,
        track_history: labelDraft.track_history,
        category: "segmentation",
      };
      createLabelMutation.mutate(payload);
      return;
    }

    const payload: ComputedLabelUpdateRequest = {
      display_name: labelDraft.display_name.trim(),
      description: labelDraft.description.trim() || undefined,
      rules,
      default_label: labelDraft.default_label,
      label_options: options,
      track_history: labelDraft.track_history,
      is_enabled: labelDraft.is_enabled,
    };
    updateLabelMutation.mutate({ labelId: editingLabel.id, payload });
  };

  /* ── Derived data ── */

  const metrics = metricsQuery.data || [];
  const labels = labelsQuery.data || [];
  const runs = runsQuery.data || [];

  const handleToggleMetric = (metric: ComputedMetric) => {
    const next = !metric.is_active;
    updateMetricMutation.mutate({
      metricId: metric.id,
      payload: { is_active: next },
    });
  };

  const handleToggleLabel = (label: ComputedLabel) => {
    const next = !label.is_enabled;
    updateLabelMutation.mutate({
      labelId: label.id,
      payload: { is_enabled: next },
    });
  };

  const metricColumns = useMemo(
    () => buildMetricColumns({
      onEdit: openEditMetric,
      onToggle: handleToggleMetric,
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [openEditMetric, updateMetricMutation],
  );

  const labelColumns = useMemo(
    () => buildLabelColumns({
      onEdit: openEditLabel,
      onToggle: handleToggleLabel,
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [openEditLabel, updateLabelMutation],
  );

  const metricsTable = useReactTable({
    data: metrics,
    columns: metricColumns,
    getCoreRowModel: getCoreRowModel(),
    getRowId: (row) => row.id,
  });

  const labelsTable = useReactTable({
    data: labels,
    columns: labelColumns,
    getCoreRowModel: getCoreRowModel(),
    getRowId: (row) => row.id,
  });

  const isSavingMetric = createMetricMutation.isLoading || updateMetricMutation.isLoading;

  const RECOMPUTE_COOLDOWN_MS = 30 * 60 * 1000; // 30 minutes
  const recomputeCooldown = useMemo(() => {
    // Check local trigger timestamp (covers the gap before refresh)
    const localElapsed = lastFullRecomputeAt.current ? Date.now() - lastFullRecomputeAt.current : Infinity;
    // Check server runs list
    const lastFullRun = runs.find((r) => r.run_type === "full");
    const serverElapsed = lastFullRun?.started_at ? Date.now() - new Date(lastFullRun.started_at).getTime() : Infinity;
    const elapsed = Math.min(localElapsed, serverElapsed);
    if (elapsed < RECOMPUTE_COOLDOWN_MS) {
      return { onCooldown: true, minutesLeft: Math.ceil((RECOMPUTE_COOLDOWN_MS - elapsed) / 60000) };
    }
    return { onCooldown: false, minutesLeft: 0 };
  }, [runs]);
  const isSavingLabel = createLabelMutation.isLoading || updateLabelMutation.isLoading;

  const canProceedMetricStep1 = useMemo(
    () => {
      if (!metricDraft.display_name.trim()) return false;
      if (editingMetric) return true;
      return Boolean(metricDraft.name.trim());
    },
    [editingMetric, metricDraft.display_name, metricDraft.name],
  );

  const canProceedMetricStep2 = useMemo(
    () => {
      try {
        buildMetricFormula(metricDraft);
        return true;
      } catch {
        return false;
      }
    },
    [metricDraft],
  );

  const metricStepLabel = useMemo(
    () => (metricModalStep === 1 ? "Basic Info" : metricModalStep === 2 ? "Formula" : "Review"),
    [metricModalStep],
  );

  const metricFormulaLabel = useMemo(
    () => FORMULA_TYPE_LABELS[metricDraft.formula_type] || metricDraft.formula_type,
    [metricDraft.formula_type],
  );

  if (!workspaceId) {
    return <div className="p-6">Workspace not found.</div>;
  }

  /* ──────────────────── Render helpers ──────────────────── */

  const renderTable = <T,>(
    table: ReturnType<typeof useReactTable<T>>,
    isLoading: boolean,
    emptyMessage: string,
    onRowClick: (row: T) => void,
  ) => (
    <div className="overflow-x-auto rounded-md border border-border">
      {isLoading ? (
        <div className="flex items-center justify-center py-10">
          <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
        </div>
      ) : (
        <table className="w-full min-w-[680px]">
          <thead>
            {table.getHeaderGroups().map((headerGroup) => (
              <tr key={headerGroup.id} className="border-b border-border">
                {headerGroup.headers.map((header) => {
                  const align = header.column.id === "actions" ? "text-right" : "text-left";
                  return (
                    <th key={header.id} className={`px-3 py-2 text-[11px] font-medium text-muted-foreground ${align}`}>
                      {header.isPlaceholder ? null : flexRender(header.column.columnDef.header, header.getContext())}
                    </th>
                  );
                })}
              </tr>
            ))}
          </thead>
          <tbody className="divide-y divide-border">
            {table.getRowModel().rows.length === 0 ? (
              <tr>
                <td colSpan={table.getAllLeafColumns().length} className="px-3 py-8 text-center text-[13px] text-muted-foreground">
                  {emptyMessage}
                </td>
              </tr>
            ) : (
              table.getRowModel().rows.map((row) => (
                <tr
                  key={row.id}
                  className="group cursor-pointer transition-colors hover:bg-muted/40"
                  onClick={() => onRowClick(row.original)}
                >
                  {row.getVisibleCells().map((cell) => {
                    const align = cell.column.id === "actions" ? "text-right" : "text-left";
                    return (
                      <td key={cell.id} className={`px-3 py-2.5 ${align}`}>
                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                      </td>
                    );
                  })}
                </tr>
              ))
            )}
          </tbody>
        </table>
      )}
    </div>
  );

  /* ──────────────────── JSX ──────────────────── */

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-lg font-medium text-foreground">Computed Traits</h1>
          <p className="text-[13px] text-muted-foreground mt-1">Automatically calculated metrics and labels</p>
        </div>
        <Button size="sm" onClick={() => (activeTab === "metrics" ? openCreateMetric() : openCreateLabel())}>
          <Plus className="mr-1.5 h-3.5 w-3.5" />
          New {activeTab === "metrics" ? "Metric" : "Label"}
        </Button>
      </div>

      {/* Explanatory callout */}
      <div className="flex items-start gap-2.5 rounded-md border border-border bg-muted/30 px-3 py-2.5">
        <Info className="mt-0.5 h-4 w-4 flex-shrink-0 text-muted-foreground" />
        <p className="text-[12px] leading-relaxed text-muted-foreground">
          Computed traits are automatically calculated metrics and labels that update daily.
          Metrics aggregate event data (counts, growth rates) while labels classify accounts/users into categories based on rules.
        </p>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={(v) => setActiveTab(v as ActiveComputedTraitTab)}>
        <TabsList>
          <TabsTrigger value="metrics">Metrics</TabsTrigger>
          <TabsTrigger value="labels">Labels</TabsTrigger>
        </TabsList>

        <TabsContent value="metrics">
          <div className="space-y-6">
            {renderTable(metricsTable, metricsQuery.isLoading, "No computed metrics yet.", openEditMetric)}

            {/* Computation Runs */}
            <div>
              <div className="mb-3 flex items-center justify-between">
                <h3 className="text-[13px] font-medium text-foreground">Computation Runs</h3>
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => { runsQuery.refetch().catch(() => undefined); }}
                    disabled={runsQuery.isFetching}
                  >
                    {runsQuery.isFetching && <Loader2 className="mr-1.5 h-3 w-3 animate-spin" />}
                    Refresh
                  </Button>
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <span className="inline-flex">
                          <Button
                            size="sm"
                            onClick={() => triggerRecomputeMutation.mutate(true)}
                            disabled={triggerRecomputeMutation.isLoading || recomputeCooldown.onCooldown}
                          >
                            {triggerRecomputeMutation.isLoading && <Loader2 className="mr-1.5 h-3 w-3 animate-spin" />}
                            Full Recompute
                          </Button>
                        </span>
                      </TooltipTrigger>
                      <TooltipContent>
                        {recomputeCooldown.onCooldown
                          ? `A full recompute was triggered recently. Please wait ${recomputeCooldown.minutesLeft} min before triggering another.`
                          : "Recompute all metrics and labels from scratch. Incremental runs happen automatically."}
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                </div>
              </div>

              <div className="overflow-hidden rounded-md border border-border">
                {runsQuery.isLoading ? (
                  <div className="flex items-center justify-center py-10">
                    <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
                  </div>
                ) : runs.length === 0 ? (
                  <div className="px-3 py-8 text-center text-[13px] text-muted-foreground">No runs yet.</div>
                ) : (
                  <div className="divide-y divide-border">
                    {runs.map((run) => (
                      <div key={run.id} className="flex items-center gap-6 px-3 py-2.5 text-[13px]">
                        <div className="flex items-center gap-1.5 min-w-[110px]">
                          <div className={`w-1.5 h-1.5 rounded-full ${run.status === "completed" ? "bg-primary" : run.status === "failed" ? "bg-destructive" : "bg-amber-500"}`} />
                          <span className="text-muted-foreground">{run.status}</span>
                        </div>
                        <span className="text-muted-foreground min-w-[125px]">{formatDateTime(run.started_at)}</span>
                        <span className="text-muted-foreground">{run.run_type}</span>
                        <span className="text-muted-foreground ml-auto">{formatDuration(run.duration_ms)}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        </TabsContent>

        <TabsContent value="labels">
          {renderTable(labelsTable, labelsQuery.isLoading, "No computed labels yet.", openEditLabel)}
        </TabsContent>
      </Tabs>

      {/* ─── Metric Modal ─── */}
      <Dialog open={metricModalOpen} onOpenChange={(open) => { if (!open) closeMetricModal(); }}>
        <DialogContent className="max-w-[580px] max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingMetric ? "Edit Computed Metric" : "Create Computed Metric"}</DialogTitle>
            <DialogDescription>
              {editingMetric
                ? "Update the editable fields for this metric. Name, entity, data type, and category are locked after creation."
                : "Define a new computed metric in three steps."}
            </DialogDescription>
          </DialogHeader>

          {/* Step indicators */}
          <div className="flex items-center gap-2">
            {METRIC_MODAL_STEPS.map((step, index) => (
              <React.Fragment key={step}>
                <div
                  className={`flex h-6 w-6 items-center justify-center rounded-full text-[11px] font-medium ${
                    step <= metricModalStep ? "bg-primary text-white" : "bg-muted text-muted-foreground"
                  }`}
                >
                  {step < metricModalStep ? <Check className="h-3 w-3" /> : step}
                </div>
                {index < METRIC_MODAL_STEPS.length - 1 && (
                  <div className={`h-px w-8 ${step < metricModalStep ? "bg-primary" : "bg-border"}`} />
                )}
              </React.Fragment>
            ))}
            <span className="ml-2 text-[12px] text-muted-foreground">{metricStepLabel}</span>
          </div>

          <Separator />

          {/* Step 1 — Basic Info */}
          {metricModalStep === 1 && (
            <div className="space-y-4">
              {/* Info callout for create mode */}
              {!editingMetric && (
                <div className="flex items-start gap-2 rounded-md border border-border bg-muted/30 px-3 py-2">
                  <Info className="mt-0.5 h-3.5 w-3.5 flex-shrink-0 text-muted-foreground" />
                  <p className="text-[11px] leading-relaxed text-muted-foreground">
                    Give your metric a display name — the internal identifier (<code className="font-mono">c__your_name</code>) is generated automatically and cannot be changed after creation.
                  </p>
                </div>
              )}

              {/* Read-only info when editing */}
              {editingMetric && (
                <div className="flex flex-wrap items-center gap-2">
                  <Badge variant="outline" className="text-[11px]">
                    <code className="font-mono">{editingMetric.column_name}</code>
                  </Badge>
                  <Badge variant="secondary" className="text-[11px]">{editingMetric.entity_type}</Badge>
                  <Badge variant="secondary" className="text-[11px]">{editingMetric.data_type}</Badge>
                  <Badge variant="secondary" className="text-[11px]">{editingMetric.category || "activity"}</Badge>
                </div>
              )}

              {/* Display Name → auto-generates Name (create); only Display Name when editing */}
              <FieldRow label="Display Name" hint={!editingMetric ? `Internal name: c__${metricDraft.name || "your_name"}` : "Human-readable name shown in the UI"}>
                <Input
                  placeholder="Health Score"
                  value={metricDraft.display_name}
                  onChange={(e) => {
                    const displayName = e.target.value;
                    if (!editingMetric) {
                      setMetricDraft((c) => ({
                        ...c,
                        display_name: displayName,
                        name: normalizeName(displayName),
                      }));
                    } else {
                      setMetricDraft((c) => ({ ...c, display_name: displayName }));
                    }
                  }}
                />
              </FieldRow>

              {/* Entity + Category — only in create mode */}
              {!editingMetric && (
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                  <FieldRow label="Entity" hint="Which object this metric applies to">
                    <Select
                      value={metricDraft.entity_type}
                      onValueChange={(v) => setMetricDraft((c) => ({ ...c, entity_type: coerceSelectValue(v, ENTITY_TYPE_VALUES, "accounts") }))}
                    >
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="accounts">accounts</SelectItem>
                        <SelectItem value="users">users</SelectItem>
                      </SelectContent>
                    </Select>
                  </FieldRow>
                  <FieldRow label="Category" hint="Activity = event-based metrics, Object = related object aggregations, Segmentation = scoring and classification">
                    <Select
                      value={metricDraft.category}
                      onValueChange={(v) => setMetricDraft((c) => ({ ...c, category: coerceSelectValue(v, CATEGORY_VALUES, "activity") }))}
                    >
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="activity">Activity</SelectItem>
                        <SelectItem value="object">Object</SelectItem>
                        <SelectItem value="segmentation">Segmentation</SelectItem>
                      </SelectContent>
                    </Select>
                  </FieldRow>
                </div>
              )}

              {/* Display Format + Switches */}
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                <FieldRow label="Display Format" hint="How the value is formatted in the UI">
                  <Select
                    value={metricDraft.display_format}
                    onValueChange={(v) => setMetricDraft((c) => ({ ...c, display_format: coerceSelectValue(v, DISPLAY_FORMAT_VALUES, "number") }))}
                  >
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="number"># number</SelectItem>
                      <SelectItem value="percentage">% percent</SelectItem>
                      <SelectItem value="currency">$ currency</SelectItem>
                      <SelectItem value="decimal_1">.1 decimal</SelectItem>
                      <SelectItem value="decimal_2">.00 decimal</SelectItem>
                      <SelectItem value="duration_seconds">duration</SelectItem>
                    </SelectContent>
                  </Select>
                </FieldRow>

                {editingMetric && (
                  <FieldRow label="Compute Priority" hint="Lower values are computed first">
                    <Input
                      type="number"
                      min={1}
                      value={metricDraft.compute_priority}
                      onChange={(e) => setMetricDraft((c) => ({ ...c, compute_priority: Number(e.target.value || 100) }))}
                    />
                  </FieldRow>
                )}
              </div>

              <div className="flex items-center gap-6">
                <div className="flex items-center gap-2">
                  <Switch
                    checked={metricDraft.track_history}
                    onCheckedChange={(v) => setMetricDraft((c) => ({ ...c, track_history: v }))}
                  />
                  <Label className="text-[12px]">Track History</Label>
                </div>
                {editingMetric && (
                  <div className="flex items-center gap-2">
                    <Switch
                      checked={metricDraft.is_active}
                      onCheckedChange={(v) => setMetricDraft((c) => ({ ...c, is_active: v }))}
                    />
                    <Label className="text-[12px]">Active</Label>
                  </div>
                )}
              </div>

              <FieldRow label="Description" hint="Optional — describe what this metric measures">
                <Textarea
                  rows={2}
                  placeholder="What does this metric measure?"
                  value={metricDraft.description}
                  onChange={(e) => setMetricDraft((c) => ({ ...c, description: e.target.value }))}
                />
              </FieldRow>
            </div>
          )}

          {/* Step 2 — Formula */}
          {metricModalStep === 2 && (
            <div className="space-y-4">
              <div>
                <Label className="text-[12px] mb-2 block">Formula Type</Label>
                <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                  {METRIC_FORMULA_TYPE_CONFIG.map((item) => (
                    <button
                      key={item.value}
                      type="button"
                      onClick={() => setMetricDraft((c) => ({ ...c, formula_type: item.value }))}
                      className={`rounded-md border p-2.5 text-left transition-colors ${
                        metricDraft.formula_type === item.value
                          ? "border-primary bg-primary/5"
                          : "border-border hover:border-foreground/20"
                      }`}
                    >
                      <span className="text-[12px] font-semibold">{item.label}</span>
                      <span className="block text-[10px] text-muted-foreground mt-0.5">{item.description}</span>
                    </button>
                  ))}
                </div>
              </div>

              {/* Contextual help for selected formula type */}
              <div className="flex items-start gap-2 rounded-md border border-border bg-muted/30 px-3 py-2">
                <Info className="mt-0.5 h-3.5 w-3.5 flex-shrink-0 text-muted-foreground" />
                <p className="text-[11px] leading-relaxed text-muted-foreground">
                  {FORMULA_TYPE_HELP[metricDraft.formula_type]}
                </p>
              </div>

              <div className="rounded-md border border-border p-3 space-y-3">
                {/* Event Aggregation / Event Growth */}
                {(metricDraft.formula_type === "event_aggregation" || metricDraft.formula_type === "event_growth") && (
                  <>
                    <TagsField
                      label="Event Names"
                      hint="Select one or more metrics for the selected entity, or use * for all"
                      values={metricDraft.event_names.trim() === "*" ? ["*"] : splitComma(metricDraft.event_names)}
                      options={["*", ...metricEventNameOptions]}
                      onChange={(values) =>
                        setMetricDraft((c) => {
                          if (values.includes("*")) return { ...c, event_names: "*" };
                          return { ...c, event_names: values.join(", ") };
                        })}
                    />
                    <div className="grid grid-cols-2 gap-3">
                      <FieldRow label="Operation">
                        <Select
                          value={metricDraft.event_operation}
                          onValueChange={(v) => setMetricDraft((c) => ({ ...c, event_operation: coerceSelectValue(v, EVENT_OPERATIONS, "count") }))}
                        >
                          <SelectTrigger><SelectValue /></SelectTrigger>
                          <SelectContent>
                            {EVENT_OPERATIONS.map((op) => <SelectItem key={op} value={op}>{op}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      </FieldRow>
                      <FieldRow label="Time Window (days)" hint={metricDraft.formula_type === "event_growth" ? "Required for growth" : undefined}>
                        <Select
                          value={metricDraft.event_days || (metricDraft.formula_type === "event_growth" ? "" : "all_time")}
                          onValueChange={(v) => setMetricDraft((c) => ({ ...c, event_days: v === "all_time" ? "" : v }))}
                        >
                          <SelectTrigger><SelectValue placeholder={metricDraft.formula_type === "event_growth" ? "Select…" : "All time"} /></SelectTrigger>
                          <SelectContent>
                            {metricDraft.formula_type !== "event_growth" && <SelectItem value="all_time">All time</SelectItem>}
                            <SelectItem value="1">1 day</SelectItem>
                            <SelectItem value="7">7 days</SelectItem>
                            <SelectItem value="14">14 days</SelectItem>
                            <SelectItem value="30">30 days</SelectItem>
                            <SelectItem value="60">60 days</SelectItem>
                            <SelectItem value="90">90 days</SelectItem>
                            <SelectItem value="180">180 days</SelectItem>
                            <SelectItem value="365">365 days</SelectItem>
                          </SelectContent>
                        </Select>
                      </FieldRow>
                    </div>
                    {metricDraft.formula_type === "event_aggregation" && (
                      <FieldRow label="Offset (days)" hint="Shift the time window back by this many days">
                        <Input
                          type="number"
                          min={0}
                          value={metricDraft.event_days_offset}
                          onChange={(e) => setMetricDraft((c) => ({ ...c, event_days_offset: Number(e.target.value || 0) }))}
                        />
                      </FieldRow>
                    )}
                    {metricDraft.formula_type === "event_growth" && (
                      <FieldRow label="Comparison">
                        <Select
                          value={metricDraft.event_comparison}
                          onValueChange={(v) => setMetricDraft((c) => ({ ...c, event_comparison: coerceSelectValue(v, GROWTH_COMPARISON_VALUES, "percentage_change") }))}
                        >
                          <SelectTrigger><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="percentage_change">percentage_change</SelectItem>
                            <SelectItem value="absolute_change">absolute_change</SelectItem>
                            <SelectItem value="ratio">ratio</SelectItem>
                          </SelectContent>
                        </Select>
                      </FieldRow>
                    )}
                  </>
                )}

                {/* Object Aggregation */}
                {metricDraft.formula_type === "object_aggregation" && (
                  <>
                    <div className="grid grid-cols-2 gap-3">
                      <FieldRow label="Object Slug">
                        <Select
                          value={metricDraft.object_slug}
                          onValueChange={(v) => setMetricDraft((c) => ({ ...c, object_slug: v || "" }))}
                        >
                          <SelectTrigger><SelectValue placeholder="Select object…" /></SelectTrigger>
                          <SelectContent>
                            {objectOptions.map((opt) => <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      </FieldRow>
                      <FieldRow label="Operation">
                        <Select
                          value={metricDraft.object_operation}
                          onValueChange={(v) => setMetricDraft((c) => ({ ...c, object_operation: coerceSelectValue(v, OBJECT_OPERATIONS, "count") }))}
                        >
                          <SelectTrigger><SelectValue /></SelectTrigger>
                          <SelectContent>
                            {OBJECT_OPERATIONS.map((op) => <SelectItem key={op} value={op}>{op}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      </FieldRow>
                    </div>
                    {["sum", "avg", "min", "max"].includes(metricDraft.object_operation) && (
                      <FieldRow label="Field" hint="The numeric field to aggregate">
                        <Input
                          placeholder="amount"
                          value={metricDraft.object_field}
                          onChange={(e) => setMetricDraft((c) => ({ ...c, object_field: e.target.value }))}
                        />
                      </FieldRow>
                    )}
                    <FieldRow label="Time Window (days)">
                      <Select
                        value={metricDraft.object_days || "all_time"}
                        onValueChange={(v) => setMetricDraft((c) => ({ ...c, object_days: v === "all_time" ? "" : v }))}
                      >
                        <SelectTrigger><SelectValue placeholder="All time" /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all_time">All time</SelectItem>
                          <SelectItem value="1">1 day</SelectItem>
                          <SelectItem value="7">7 days</SelectItem>
                          <SelectItem value="14">14 days</SelectItem>
                          <SelectItem value="30">30 days</SelectItem>
                          <SelectItem value="60">60 days</SelectItem>
                          <SelectItem value="90">90 days</SelectItem>
                          <SelectItem value="180">180 days</SelectItem>
                          <SelectItem value="365">365 days</SelectItem>
                        </SelectContent>
                      </Select>
                    </FieldRow>
                  </>
                )}

                {/* Derived Formula */}
                {metricDraft.formula_type === "formula" && (
                  <>
                    <FieldRow label="Expression">
                      <Select
                        value={metricDraft.formula_expression}
                        onValueChange={(v) => setMetricDraft((c) => ({ ...c, formula_expression: coerceSelectValue(v, FORMULA_EXPRESSIONS, "add") }))}
                      >
                        <SelectTrigger><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {FORMULA_EXPRESSIONS.map((expr) => <SelectItem key={expr} value={expr}>{expr}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </FieldRow>
                    <div className="space-y-2">
                      {metricDraft.operands.map((operand, index) => (
                        <div key={operand.id} className="flex items-end gap-2">
                          <div className="flex-1">
                            {index === 0 && <Label className="text-[12px] mb-1 block">Operands</Label>}
                            <Select
                              value={operand.kind}
                              onValueChange={(v) =>
                                setMetricDraft((c) => {
                                  const next = [...c.operands];
                                  next[index] = { ...next[index], kind: coerceSelectValue(v, OPERAND_KIND_VALUES, "ref") };
                                  return { ...c, operands: next };
                                })}
                            >
                              <SelectTrigger><SelectValue /></SelectTrigger>
                              <SelectContent>
                                <SelectItem value="ref">Metric Ref</SelectItem>
                                <SelectItem value="prop">Property</SelectItem>
                                <SelectItem value="raw">Raw Number</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                          <div className="flex-1">
                            <Input
                              placeholder={operand.kind === "raw" ? "0" : operand.kind === "prop" ? "property_name" : "c__metric_name"}
                              value={operand.value}
                              onChange={(e) =>
                                setMetricDraft((c) => {
                                  const next = [...c.operands];
                                  next[index] = { ...next[index], value: e.target.value };
                                  return { ...c, operands: next };
                                })}
                            />
                          </div>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-7 w-7 text-destructive hover:text-destructive flex-shrink-0"
                            disabled={metricDraft.operands.length <= 2}
                            onClick={() =>
                              setMetricDraft((c) => ({
                                ...c,
                                operands: c.operands.filter((_, i) => i !== index),
                              }))}
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                          </Button>
                        </div>
                      ))}
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() =>
                          setMetricDraft((c) => ({
                            ...c,
                            operands: [...c.operands, { id: createDraftId(), kind: "ref", value: "" }],
                          }))}
                      >
                        <Plus className="mr-1.5 h-3 w-3" /> Add Operand
                      </Button>
                    </div>
                  </>
                )}
              </div>
            </div>
          )}

          {/* Step 3 — Review */}
          {metricModalStep === 3 && (
            <div className="space-y-3">
              <div className="rounded-md border border-border bg-muted/30 p-4">
                <div className="grid grid-cols-2 gap-y-2 text-[12px]">
                  <span className="text-muted-foreground">Name</span>
                  <code className="font-mono text-[12px]">{`c__${metricDraft.name}`}</code>

                  <span className="text-muted-foreground">Display Name</span>
                  <span>{metricDraft.display_name || "—"}</span>

                  <span className="text-muted-foreground">Entity</span>
                  <span>{metricDraft.entity_type}</span>

                  <span className="text-muted-foreground">Data Type</span>
                  <span>{metricDraft.data_type}</span>

                  <span className="text-muted-foreground">Category</span>
                  <span>{metricDraft.category}</span>

                  <span className="text-muted-foreground">Display Format</span>
                  <span>{metricDraft.display_format}</span>

                  <span className="text-muted-foreground">Track History</span>
                  <span>{metricDraft.track_history ? "Yes" : "No"}</span>

                  <span className="text-muted-foreground">Formula Type</span>
                  <span>{metricFormulaLabel}</span>
                </div>
                {metricDraft.description.trim() && (
                  <div className="mt-3 border-t border-border pt-3">
                    <span className="text-[10px] text-muted-foreground block mb-1">Description</span>
                    <span className="text-[12px]">{metricDraft.description}</span>
                  </div>
                )}
              </div>

              <div className="rounded-md border border-border p-3">
                <span className="text-[11px] font-semibold block mb-2">Formula Configuration</span>
                {(metricDraft.formula_type === "event_aggregation" || metricDraft.formula_type === "event_growth") && (
                  <div className="space-y-1 text-[12px]">
                    <p><span className="text-muted-foreground">Events:</span> <code>{metricDraft.event_names || "*"}</code></p>
                    <p><span className="text-muted-foreground">Operation:</span> {metricDraft.event_operation}</p>
                    {metricDraft.event_days && <p><span className="text-muted-foreground">Window:</span> {metricDraft.event_days} days</p>}
                    {metricDraft.formula_type === "event_growth" && (
                      <p><span className="text-muted-foreground">Comparison:</span> {metricDraft.event_comparison}</p>
                    )}
                  </div>
                )}
                {metricDraft.formula_type === "object_aggregation" && (
                  <div className="space-y-1 text-[12px]">
                    <p><span className="text-muted-foreground">Object:</span> {metricDraft.object_slug || "—"}</p>
                    <p><span className="text-muted-foreground">Operation:</span> {metricDraft.object_operation}</p>
                    {metricDraft.object_field && <p><span className="text-muted-foreground">Field:</span> {metricDraft.object_field}</p>}
                    {metricDraft.object_days && <p><span className="text-muted-foreground">Window:</span> {metricDraft.object_days} days</p>}
                  </div>
                )}
                {metricDraft.formula_type === "formula" && (
                  <div className="space-y-1 text-[12px]">
                    <p><span className="text-muted-foreground">Expression:</span> {metricDraft.formula_expression}</p>
                    <p>
                      <span className="text-muted-foreground">Operands:</span> {metricDraft.operands.filter((o) => o.value.trim()).length}
                    </p>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Footer navigation */}
          <div className="flex items-center justify-between pt-2">
            <div>
              {metricModalStep > 1 && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setMetricModalStep((c) => (Math.max(1, c - 1) as MetricModalStep))}
                >
                  <ArrowLeft className="mr-1.5 h-3.5 w-3.5" /> Back
                </Button>
              )}
            </div>
            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm" onClick={closeMetricModal}>
                Cancel
              </Button>
              {metricModalStep < 3 ? (
                <Button
                  size="sm"
                  disabled={metricModalStep === 1 ? !canProceedMetricStep1 : !canProceedMetricStep2}
                  onClick={() => setMetricModalStep((c) => (Math.min(3, c + 1) as MetricModalStep))}
                >
                  Next <ArrowRight className="ml-1.5 h-3.5 w-3.5" />
                </Button>
              ) : (
                <Button size="sm" onClick={handleSaveMetric} disabled={isSavingMetric}>
                  {isSavingMetric && <Loader2 className="mr-1.5 h-3 w-3 animate-spin" />}
                  {editingMetric ? "Save Changes" : "Create Metric"}
                </Button>
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* ─── Label Modal ─── */}
      <Dialog open={labelModalOpen} onOpenChange={(open) => { if (!open) setLabelModalOpen(false); }}>
        <DialogContent className="max-w-[640px] max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingLabel ? "Edit Computed Label" : "Create Computed Label"}</DialogTitle>
            <DialogDescription>
              Labels classify accounts or users into categories. Define label options (e.g. Healthy, At Risk, Churning), then create rules that automatically assign labels based on conditions.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            {/* Read-only info — edit mode only */}
            {editingLabel && (
              <div className="flex flex-wrap items-center gap-2">
                <Badge variant="outline" className="text-[11px]">
                  <code className="font-mono">{editingLabel.column_name}</code>
                </Badge>
                <Badge variant="secondary" className="text-[11px]">{editingLabel.entity_type}</Badge>
              </div>
            )}

            {/* Display Name → auto-generates Name (create); only Display Name when editing */}
            <FieldRow label="Display Name" hint={!editingLabel ? `Internal name: c__${labelDraft.name || "your_name"}` : undefined}>
              <Input
                placeholder="Health Bucket"
                value={labelDraft.display_name}
                onChange={(e) => {
                  const displayName = e.target.value;
                  if (!editingLabel) {
                    setLabelDraft((c) => ({
                      ...c,
                      display_name: displayName,
                      name: normalizeName(displayName),
                    }));
                  } else {
                    setLabelDraft((c) => ({ ...c, display_name: displayName }));
                  }
                }}
              />
            </FieldRow>

            <FieldRow label="Description">
              <Textarea
                rows={2}
                value={labelDraft.description}
                onChange={(e) => setLabelDraft((c) => ({ ...c, description: e.target.value }))}
              />
            </FieldRow>

            {/* Entity + Default Label */}
            <div className="grid grid-cols-2 gap-3">
              {!editingLabel ? (
                <FieldRow label="Entity" hint="Which object this label applies to">
                  <Select
                    value={labelDraft.entity_type}
                    onValueChange={(v) => setLabelDraft((c) => ({ ...c, entity_type: coerceSelectValue(v, ENTITY_TYPE_VALUES, "accounts") }))}
                  >
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="accounts">accounts</SelectItem>
                      <SelectItem value="users">users</SelectItem>
                    </SelectContent>
                  </Select>
                </FieldRow>
              ) : (
                <div />
              )}
              <FieldRow label="Default Label" hint="Assigned when no rule matches">
                <Select
                  value={labelDraft.default_label}
                  onValueChange={(v) => setLabelDraft((c) => ({ ...c, default_label: v || "" }))}
                >
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {labelDraft.options.filter((opt) => opt.value).map((opt) => (
                      <SelectItem key={opt.value} value={opt.value}>{opt.display_name || opt.value}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </FieldRow>
            </div>

            {/* Switches */}
            <div className="flex items-center gap-6">
              <div className="flex items-center gap-2">
                <Switch
                  checked={labelDraft.track_history}
                  onCheckedChange={(v) => setLabelDraft((c) => ({ ...c, track_history: v }))}
                />
                <Label className="text-[12px]">Track History</Label>
              </div>
              {editingLabel && (
                <div className="flex items-center gap-2">
                  <Switch
                    checked={labelDraft.is_enabled}
                    onCheckedChange={(v) => setLabelDraft((c) => ({ ...c, is_enabled: v }))}
                  />
                  <Label className="text-[12px]">Enabled</Label>
                </div>
              )}
            </div>

            <Separator />

            {/* Label Options */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <Label className="text-[13px] font-semibold">Label Options</Label>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() =>
                    setLabelDraft((c) => ({
                      ...c,
                      options: [...c.options, { id: createDraftId(), value: "", display_name: "", color: "#3b82f6" }],
                    }))}
                >
                  <Plus className="mr-1 h-3 w-3" /> Add Option
                </Button>
              </div>
              <div className="space-y-2">
                {labelDraft.options.map((option, optionIndex) => (
                  <div key={option.id} className="flex items-end gap-2">
                    <div className="flex-1">
                      {optionIndex === 0 && <Label className="text-[11px] mb-1 block">Value</Label>}
                      <Input
                        value={option.value}
                        onChange={(e) =>
                          setLabelDraft((c) => {
                            const next = [...c.options];
                            next[optionIndex] = { ...next[optionIndex], value: normalizeName(e.target.value) };
                            return { ...c, options: next };
                          })}
                      />
                    </div>
                    <div className="flex-1">
                      {optionIndex === 0 && <Label className="text-[11px] mb-1 block">Display Name</Label>}
                      <Input
                        value={option.display_name}
                        onChange={(e) =>
                          setLabelDraft((c) => {
                            const next = [...c.options];
                            next[optionIndex] = { ...next[optionIndex], display_name: e.target.value };
                            return { ...c, options: next };
                          })}
                      />
                    </div>
                    <div className="w-20 flex-shrink-0">
                      {optionIndex === 0 && <Label className="text-[11px] mb-1 block">Color</Label>}
                      <div className="flex items-center gap-1.5">
                        <label className="relative h-8 w-8 flex-shrink-0 cursor-pointer rounded border border-border" style={{ backgroundColor: option.color }}>
                          <input
                            type="color"
                            className="absolute inset-0 h-full w-full cursor-pointer opacity-0"
                            value={option.color}
                            onChange={(e) =>
                              setLabelDraft((c) => {
                                const next = [...c.options];
                                next[optionIndex] = { ...next[optionIndex], color: e.target.value };
                                return { ...c, options: next };
                              })}
                          />
                        </label>
                        <span className="text-[11px] font-mono text-muted-foreground">{option.color}</span>
                      </div>
                    </div>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-7 w-7 text-destructive hover:text-destructive flex-shrink-0"
                      disabled={labelDraft.options.length <= 2}
                      onClick={() =>
                        setLabelDraft((c) => ({
                          ...c,
                          options: c.options.filter((_, i) => i !== optionIndex),
                        }))}
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                ))}
              </div>
            </div>

            <Separator />

            {/* Rules */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <Label className="text-[13px] font-semibold">Rules</Label>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() =>
                    setLabelDraft((c) => ({
                      ...c,
                      rules: [
                        ...c.rules,
                        {
                          id: createDraftId(),
                          label: c.options[0]?.value || "",
                          match_type: "all",
                          predicates: [createEmptyPredicateDraft()],
                        },
                      ],
                    }))}
                >
                  <Plus className="mr-1 h-3 w-3" /> Add Rule
                </Button>
              </div>

              <div className="space-y-3">
                {labelDraft.rules.map((rule, ruleIndex) => (
                  <div key={rule.id} className="rounded-md border border-border p-3 space-y-3">
                    <div className="flex items-end gap-2">
                      <div className="flex-1">
                        <Label className="text-[11px] mb-1 block">Assign Label</Label>
                        <Select
                          value={rule.label}
                          onValueChange={(v) =>
                            setLabelDraft((c) => {
                              const next = [...c.rules];
                              next[ruleIndex] = { ...next[ruleIndex], label: v || "" };
                              return { ...c, rules: next };
                            })}
                        >
                          <SelectTrigger><SelectValue /></SelectTrigger>
                          <SelectContent>
                            {labelDraft.options.filter((opt) => opt.value).map((opt) => (
                              <SelectItem key={opt.value} value={opt.value}>{opt.display_name || opt.value}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="flex-1">
                        <Label className="text-[11px] mb-1 block">Match Type</Label>
                        <Select
                          value={rule.match_type}
                          onValueChange={(v) =>
                            setLabelDraft((c) => {
                              const next = [...c.rules];
                              next[ruleIndex] = { ...next[ruleIndex], match_type: coerceSelectValue(v, RULE_MATCH_VALUES, "all") };
                              return { ...c, rules: next };
                            })}
                        >
                          <SelectTrigger><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="all">ALL (AND)</SelectItem>
                            <SelectItem value="any">ANY (OR)</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-7 w-7 text-destructive hover:text-destructive flex-shrink-0"
                        onClick={() =>
                          setLabelDraft((c) => ({
                            ...c,
                            rules: c.rules.filter((_, i) => i !== ruleIndex),
                          }))}
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    </div>

                    <div className="space-y-2">
                      {rule.predicates.map((predicate, predicateIndex) => (
                        <div key={predicate.id} className="flex items-end gap-2">
                          <div className="flex-1">
                            {predicateIndex === 0 && <Label className="text-[11px] mb-1 block">Field</Label>}
                            <Input
                              placeholder="c__health_score"
                              value={predicate.field}
                              onChange={(e) =>
                                setLabelDraft((c) => {
                                  const nextRules = [...c.rules];
                                  const nextPredicates = [...nextRules[ruleIndex].predicates];
                                  nextPredicates[predicateIndex] = { ...nextPredicates[predicateIndex], field: e.target.value };
                                  nextRules[ruleIndex] = { ...nextRules[ruleIndex], predicates: nextPredicates };
                                  return { ...c, rules: nextRules };
                                })}
                            />
                          </div>
                          <div className="w-[110px] flex-shrink-0">
                            {predicateIndex === 0 && <Label className="text-[11px] mb-1 block">Operator</Label>}
                            <Select
                              value={predicate.operator}
                              onValueChange={(v) =>
                                setLabelDraft((c) => {
                                  const nextRules = [...c.rules];
                                  const nextPredicates = [...nextRules[ruleIndex].predicates];
                                  nextPredicates[predicateIndex] = { ...nextPredicates[predicateIndex], operator: coerceSelectValue(v, PREDICATE_OPERATORS, "eq") };
                                  nextRules[ruleIndex] = { ...nextRules[ruleIndex], predicates: nextPredicates };
                                  return { ...c, rules: nextRules };
                                })}
                            >
                              <SelectTrigger><SelectValue /></SelectTrigger>
                              <SelectContent>
                                {PREDICATE_OPERATORS.map((op) => <SelectItem key={op} value={op}>{op}</SelectItem>)}
                              </SelectContent>
                            </Select>
                          </div>
                          <div className="w-[90px] flex-shrink-0">
                            {predicateIndex === 0 && <Label className="text-[11px] mb-1 block">Type</Label>}
                            <Select
                              value={predicate.type}
                              onValueChange={(v) =>
                                setLabelDraft((c) => {
                                  const nextRules = [...c.rules];
                                  const nextPredicates = [...nextRules[ruleIndex].predicates];
                                  nextPredicates[predicateIndex] = { ...nextPredicates[predicateIndex], type: coerceSelectValue(v, PREDICATE_TYPE_VALUES, "string") };
                                  nextRules[ruleIndex] = { ...nextRules[ruleIndex], predicates: nextPredicates };
                                  return { ...c, rules: nextRules };
                                })}
                            >
                              <SelectTrigger><SelectValue /></SelectTrigger>
                              <SelectContent>
                                {PREDICATE_TYPE_VALUES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                              </SelectContent>
                            </Select>
                          </div>
                          {!["known", "unknown"].includes(predicate.operator) && (
                            <div className="flex-1">
                              {predicateIndex === 0 && <Label className="text-[11px] mb-1 block">Value</Label>}
                              <Input
                                placeholder={predicate.operator === "in" || predicate.operator === "range" ? "comma,separated" : "value"}
                                value={predicate.value}
                                onChange={(e) =>
                                  setLabelDraft((c) => {
                                    const nextRules = [...c.rules];
                                    const nextPredicates = [...nextRules[ruleIndex].predicates];
                                    nextPredicates[predicateIndex] = { ...nextPredicates[predicateIndex], value: e.target.value };
                                    nextRules[ruleIndex] = { ...nextRules[ruleIndex], predicates: nextPredicates };
                                    return { ...c, rules: nextRules };
                                  })}
                              />
                            </div>
                          )}
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-7 w-7 text-destructive hover:text-destructive flex-shrink-0"
                            disabled={rule.predicates.length <= 1}
                            onClick={() =>
                              setLabelDraft((c) => {
                                const nextRules = [...c.rules];
                                const nextPredicates = nextRules[ruleIndex].predicates.filter((_, i) => i !== predicateIndex);
                                nextRules[ruleIndex] = { ...nextRules[ruleIndex], predicates: nextPredicates };
                                return { ...c, rules: nextRules };
                              })}
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                          </Button>
                        </div>
                      ))}
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() =>
                          setLabelDraft((c) => {
                            const nextRules = [...c.rules];
                            const nextPredicates: PredicateDraft[] = [
                              ...nextRules[ruleIndex].predicates,
                              createEmptyPredicateDraft(),
                            ];
                            nextRules[ruleIndex] = { ...nextRules[ruleIndex], predicates: nextPredicates };
                            return { ...c, rules: nextRules };
                          })}
                      >
                        <Plus className="mr-1 h-3 w-3" /> Add Predicate
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" size="sm" onClick={() => setLabelModalOpen(false)}>
              Cancel
            </Button>
            <Button size="sm" onClick={handleSaveLabel} disabled={isSavingLabel}>
              {isSavingLabel && <Loader2 className="mr-1.5 h-3 w-3 animate-spin" />}
              {editingLabel ? "Save Changes" : "Create Label"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ComputedTraitsPage;
